import React, { useState, useEffect } from 'react';
import { getProducts, deleteProduct } from '../services/api';
import ProductForm from './ProductForm';
import './ProductList.css';

function ProductList({ brand, user, canEdit = false, onBack }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getProducts({
        brand_id: brand?.id,
        search: searchTerm,
      });
      if (response.success) {
        setProducts(response.products);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  // Handle add new product
  const handleAddNew = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  // Handle edit product
  const handleEdit = (product, e) => {
    e?.stopPropagation();
    setEditingProduct(product);
    setShowForm(true);
  };

  // Handle delete product
  const handleDelete = async (productId) => {
    try {
      setActionLoading(true);
      const response = await deleteProduct(productId);
      if (response.success) {
        setDeleteConfirm(null);
        fetchProducts();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProduct(null);
    fetchProducts();
  };

  return (
    <div className="product-list-container">
      {/* Header with Back Button */}
      <div className="product-list-header">
        <div className="header-left">
          {onBack && (
            <button className="back-btn" onClick={onBack}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to Brands
            </button>
          )}
          <div className="header-title">
            {brand && (
              <div className="brand-badge">
                {brand.logo ? (
                  <img src={brand.logo} alt={brand.name} />
                ) : (
                  <span>{brand.name.charAt(0)}</span>
                )}
              </div>
            )}
            <div>
              <h2>{brand ? `${brand.name} Products` : 'All Products'}</h2>
              <p>Manage product catalog</p>
            </div>
          </div>
        </div>
        {canEdit && (
          <button className="add-btn" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Product
          </button>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="search-section">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search products by model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button type="submit" className="search-btn">Search</button>
      </form>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
          <h3>No products found</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Get started by adding your first product'}</p>
          {canEdit && !searchTerm && (
            <button onClick={handleAddNew}>Add Product</button>
          )}
        </div>
      ) : (
        /* Products Grid */
        <div className="products-grid">
          {products.map((product) => (
            <div 
              key={product.id} 
              className="product-card"
              onClick={() => setViewingProduct(product)}
            >
              <div className="product-image">
                {product.photos && product.photos.length > 0 ? (
                  <img src={product.photos[0]} alt={product.model} />
                ) : (
                  <div className="image-placeholder">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                )}
                {product.photos && product.photos.length > 1 && (
                  <span className="photo-count">+{product.photos.length - 1}</span>
                )}
              </div>
              <div className="product-info">
                <h3>{product.model}</h3>
                {!brand && product.brand && (
                  <span className="product-brand">{product.brand.name}</span>
                )}
                {product.usage && (
                  <p className="product-usage">{product.usage}</p>
                )}
                <div className="product-meta">
                  {product.materials && product.materials.length > 0 && (
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      {product.materials.length} files
                    </span>
                  )}
                  {product.links && product.links.length > 0 && (
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      {product.links.length} links
                    </span>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="product-actions">
                  <button 
                    className="edit-btn" 
                    onClick={(e) => handleEdit(product, e)}
                    title="Edit Product"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button 
                    className="delete-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(product);
                    }}
                    title="Delete Product"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total Count */}
      {!loading && products.length > 0 && (
        <div className="list-footer">
          <span>Total: {products.length} products</span>
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          defaultBrand={brand}
          onSuccess={handleFormSuccess}
          onClose={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
        />
      )}

      {/* Product View Modal */}
      {viewingProduct && (
        <div className="modal-overlay" onClick={() => setViewingProduct(null)}>
          <div className="product-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setViewingProduct(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <div className="product-view-content">
              {/* Photos Gallery */}
              {viewingProduct.photos && viewingProduct.photos.length > 0 && (
                <div className="product-gallery">
                  {viewingProduct.photos.map((photo, index) => (
                    <img key={index} src={photo} alt={`${viewingProduct.model} - ${index + 1}`} />
                  ))}
                </div>
              )}
              
              <div className="product-details">
                <div className="product-header">
                  {viewingProduct.brand && (
                    <span className="brand-name">{viewingProduct.brand.name}</span>
                  )}
                  <h2>{viewingProduct.model}</h2>
                </div>
                
                {viewingProduct.usage && (
                  <div className="detail-section">
                    <h4>Usage</h4>
                    <p>{viewingProduct.usage}</p>
                  </div>
                )}
                
                {viewingProduct.about && (
                  <div className="detail-section">
                    <h4>About</h4>
                    <p>{viewingProduct.about}</p>
                  </div>
                )}
                
                {viewingProduct.materials && viewingProduct.materials.length > 0 && (
                  <div className="detail-section">
                    <h4>Materials & Documents</h4>
                    <div className="materials-list">
                      {viewingProduct.materials.map((material, index) => (
                        <a 
                          key={index}
                          href={material.file_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="material-item"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          <span>{material.label}</span>
                          <small>{material.file_type}</small>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {viewingProduct.links && viewingProduct.links.length > 0 && (
                  <div className="detail-section">
                    <h4>External Links</h4>
                    <div className="links-list">
                      {viewingProduct.links.map((link, index) => (
                        <a 
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-item"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                          {link.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {canEdit && (
              <div className="product-view-actions">
                <button 
                  className="edit-btn"
                  onClick={() => {
                    setViewingProduct(null);
                    handleEdit(viewingProduct);
                  }}
                >
                  Edit Product
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>Delete Product</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.model}</strong>?</p>
            <p className="warning">All photos and materials will be deleted. This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductList;
